import { type SyntheticEvent, useEffect, useId, useRef, useSyncExternalStore } from 'react';

// The application's own confirmation dialog, instead of the browser's confirm() and prompt(): the
// same look on every device, a clear consequence, a destructive action in red, Hebrew buttons,
// focus kept inside, Escape or "ביטול" cancels. One dialog at a time; callers await the answer.

export interface ConfirmOptions {
  readonly title: string;
  /** What will happen, in one or two sentences. */
  readonly message?: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  /** A destructive action: the confirm button is red. */
  readonly danger?: boolean;
}

export interface AskTextOptions extends ConfirmOptions {
  readonly label: string;
  readonly optional?: boolean;
  readonly maxLength?: number;
}

type Request =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (answer: boolean) => void }
  | { kind: 'text'; options: AskTextOptions; resolve: (answer: string | null) => void };

let current: Request | null = null;
const queue: Request[] = [];
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};
const next = () => {
  current = queue.shift() ?? null;
  emit();
};
function enqueue(request: Request) {
  if (current === null) {
    current = request;
    emit();
  } else queue.push(request);
}

/** Asks for confirmation; resolves true only when the user confirms. */
export function askConfirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    enqueue({ kind: 'confirm', options, resolve });
  });
}

/** Asks for a short text (for example a reason); resolves null when the user cancels. */
export function askText(options: AskTextOptions): Promise<string | null> {
  return new Promise((resolve) => {
    enqueue({ kind: 'text', options, resolve });
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Rendered once, at the root of the application. */
export function ConfirmHost() {
  const request = useSyncExternalStore(subscribe, () => current);
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const messageId = useId();
  const inputId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (request !== null && !dialog.open) dialog.showModal();
    if (request === null && dialog.open) dialog.close();
  }, [request]);
  const answer = (value: boolean, text: string | null = null) => {
    const done = current;
    if (done === null) return;
    if (done.kind === 'confirm') done.resolve(value);
    else done.resolve(value ? (text ?? '') : null);
    next();
  };
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const field = new FormData(event.currentTarget).get('answer');
    answer(true, typeof field === 'string' ? field.trim() : '');
  };
  const options = request?.options;
  return (
    <dialog
      ref={ref}
      className="modal confirm-dialog"
      aria-labelledby={titleId}
      aria-describedby={options?.message === undefined ? undefined : messageId}
      onCancel={(event) => {
        event.preventDefault();
        answer(false);
      }}
    >
      {request !== null && options !== undefined && (
        <form className="confirm-dialog__body" onSubmit={submit}>
          <h2 id={titleId}>{options.title}</h2>
          {options.message !== undefined && (
            <p id={messageId} className="confirm-dialog__message">
              {options.message}
            </p>
          )}
          {request.kind === 'text' && (
            <label className="field" htmlFor={inputId}>
              <span>
                {request.options.label}
                {request.options.optional === true ? ' (רשות)' : ''}
              </span>
              <textarea
                id={inputId}
                name="answer"
                rows={3}
                maxLength={request.options.maxLength ?? 500}
                required={request.options.optional !== true}
              />
            </label>
          )}
          <div className="confirm-dialog__actions">
            <button
              type="submit"
              className={`button ${options.danger === true ? 'button--danger' : 'button--primary'}`}
              // The safe choice gets the focus first for destructive actions.
              autoFocus={options.danger !== true && request.kind === 'confirm'}
            >
              {options.confirmLabel}
            </button>
            <button
              type="button"
              className="button"
              autoFocus={options.danger === true}
              onClick={() => {
                answer(false);
              }}
            >
              {options.cancelLabel ?? 'ביטול'}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
